import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ParticlesBackground } from './ParticlesBackground';

export function HeroSection() {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed] = useState(100);
  
  const words = [
    { text: 'thoughts', gradient: 'from-blue-500 via-cyan-500 to-blue-600' },
    { text: 'blog', gradient: 'from-purple-500 via-pink-500 to-purple-600' },
    { text: 'learnings', gradient: 'from-green-500 via-emerald-500 to-green-600' },
    { text: 'journey', gradient: 'from-orange-500 via-red-500 to-orange-600' }
  ];

  useEffect(() => {
    const currentWord = words[currentWordIndex].text;
    
    if (!isDeleting && displayText === currentWord) {
      // Wait 2 seconds before starting to delete
      const timer = setTimeout(() => {
        setIsDeleting(true);
      }, 2000);
      return () => clearTimeout(timer);
    }

    if (isDeleting && displayText === '') {
      // Wait 0.5 seconds before moving to next word
      const timer = setTimeout(() => {
        setIsDeleting(false);
        setCurrentWordIndex((prevIndex) => (prevIndex + 1) % words.length);
      }, 500);
      return () => clearTimeout(timer);
    }

    // Type or delete the next character
    const timer = setTimeout(() => {
      if (isDeleting) {
        setDisplayText(currentWord.substring(0, displayText.length - 1));
      } else {
        setDisplayText(currentWord.substring(0, displayText.length + 1));
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [displayText, isDeleting, currentWordIndex, words, typingSpeed]);

  return (
    <section className="relative min-h-[300px] flex items-center justify-center py-10 overflow-hidden bg-gradient-to-b from-background via-background/95 to-background/90">
      <ParticlesBackground />
      
      <div className="relative container mx-auto px-4 text-center z-10">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-gray-100 dark:via-gray-200 dark:to-gray-100 bg-clip-text text-transparent">
            Welcome to my{' '}
          </span>
          <span className="relative inline-block">
            <span 
              className={`bg-gradient-to-r ${words[currentWordIndex].gradient} bg-clip-text text-transparent`}
            >
              {displayText}
              <span className="animate-pulse">|</span>
            </span>
          </span>
        </h1>
        
        <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
          Exploring technology, entrepreneurship, and world travel. Join me as I share insights 
          and experiences from my journey through code, business, and adventure.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/blog?category=ai">
            <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0">
              Start Reading
            </Button>
          </Link>
          <Link to="/traveling">
            <Button variant="outline" size="lg" className="backdrop-blur-sm bg-white/80 dark:bg-gray-900/80 border-gray-200/50 dark:border-gray-700/50">
              View Travel Map
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}